import React, { useEffect, useRef, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, IconButton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

interface AlertsBottomSheetProps {
  unreadCount: number;
  selectedAlertId: string | null;
  selectionSource: "map" | "sidebar" | null;
  children: React.ReactNode;
}

const AlertsBottomSheet: React.FC<AlertsBottomSheetProps> = ({
  unreadCount,
  selectedAlertId,
  selectionSource,
  children,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const touchStartY = useRef<number | null>(null);

  useEffect(() => {
    if (!selectedAlertId) return;
    setExpanded(selectionSource === "map");
  }, [selectedAlertId, selectionSource]);

  return (
    <Box
      component="section"
      aria-label={t("dashboard.sheet.region")}
      sx={{
        position: "fixed",
        zIndex: 30,
        left: 0,
        right: 0,
        bottom: 0,
        height: expanded ? "72dvh" : 92,
        borderRadius: "20px 20px 0 0",
        border: "1px solid",
        borderBottom: 0,
        borderColor: "divider",
        bgcolor: "rgba(11, 15, 23, 0.98)",
        boxShadow: "0 -18px 55px rgba(0,0,0,0.48)",
        transition: "height 240ms ease",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        pb: "env(safe-area-inset-bottom)",
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={expanded ? t("dashboard.sheet.collapse") : t("dashboard.sheet.expand")}
        onClick={() => setExpanded((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setExpanded((current) => !current);
          }
        }}
        onTouchStart={(event) => {
          touchStartY.current = event.touches[0]?.clientY ?? null;
        }}
        onTouchEnd={(event) => {
          if (touchStartY.current === null) return;
          const delta = touchStartY.current - event.changedTouches[0].clientY;
          if (Math.abs(delta) > 35) setExpanded(delta > 0);
          touchStartY.current = null;
        }}
        sx={{
          minHeight: 84,
          px: 2,
          pt: 1,
          pb: 1.5,
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 4,
            borderRadius: 999,
            bgcolor: "rgba(148, 163, 184, 0.5)",
            mx: "auto",
            mb: 1,
          }}
        />
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography fontWeight={800}>{t("dashboard.alertsTitle")}</Typography>
            <Typography variant="caption" color="text.secondary">
              {unreadCount
                ? t("dashboard.sheet.unread", { count: unreadCount })
                : t("dashboard.alertsSubtitle")}
            </Typography>
          </Box>
          <IconButton
            aria-hidden
            tabIndex={-1}
            size="small"
            sx={{ pointerEvents: "none" }}
          >
            {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
          </IconButton>
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          opacity: expanded ? 1 : 0,
          pointerEvents: expanded ? "auto" : "none",
          transition: "opacity 160ms ease",
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default AlertsBottomSheet;
